'use client';

import {
  cn,
  previewPanelSurfaceClass,
  PublicWorkspaceIdentityRow,
} from '@up2stay/up2promo-ui';

import { interpolateFeedbackMailPreview } from '@/core/lib/feedback-mail-preview';
import { workspaceBrandAccent } from '@/core/lib/workspace-brand';

type FeedbackMailPreviewProps = {
  subject: string;
  body: string;
  sample: {
    contactName: string;
    locationName: string;
    workspaceName: string;
  };
  fromLabel: string;
  toLabel: string;
  logoUrl?: string | null;
  accentColor?: string | null;
  ctaLabel?: string;
  poweredByLabel?: string;
  showPoweredBy?: boolean;
  className?: string;
};

/** Email card preview with sample merge fields filled in. */
export function FeedbackMailPreview({
  subject,
  body,
  sample,
  fromLabel,
  toLabel,
  logoUrl,
  accentColor,
  ctaLabel,
  poweredByLabel,
  showPoweredBy = true,
  className,
}: FeedbackMailPreviewProps) {
  const resolvedSubject = interpolateFeedbackMailPreview(subject, sample);
  const resolvedBody = interpolateFeedbackMailPreview(body, sample);
  const accent = workspaceBrandAccent(accentColor);

  return (
    <div
      className={cn(
        'flex h-full min-h-[320px] flex-col overflow-hidden rounded-[var(--r-card)] border shadow-[var(--shadow-card)]',
        previewPanelSurfaceClass,
        className,
      )}
    >
      <div className="space-y-1 border-b border-border/60 px-4 py-3 sm:px-5">
        <p className="truncate text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
          {fromLabel}: {sample.workspaceName}
        </p>
        <p className="truncate text-[13.5px] text-muted-foreground">
          {toLabel}: {sample.contactName}
        </p>
        <p className="truncate text-[15.5px] font-bold leading-tight text-foreground">
          {resolvedSubject || '—'}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
        <PublicWorkspaceIdentityRow
          name={sample.workspaceName}
          logoUrl={logoUrl}
          subtitle={sample.locationName}
          size="sm"
          className="mb-4"
        />
        <pre className="font-sans text-[13.5px] leading-relaxed whitespace-pre-wrap text-foreground">
          {resolvedBody || '—'}
        </pre>
        {ctaLabel ? (
          <div className="mt-6 flex justify-center">
            <span
              className="inline-flex rounded-md px-5 py-2.5 text-sm font-semibold text-white"
              style={{ backgroundColor: accent }}
            >
              {ctaLabel}
            </span>
          </div>
        ) : null}
      </div>
      {showPoweredBy && poweredByLabel ? (
        <p className="border-t border-border/50 px-4 py-2.5 text-center text-[11px] text-muted-foreground">
          {poweredByLabel}
        </p>
      ) : null}
    </div>
  );
}
